import {Descriptions, Table, Tag} from "antd"
import React from "react"
import {useTranslation} from "react-i18next"
import {
    capitalizeFirstLetter
} from "../utilities/functions"
import {useDispatch, useSelector} from "react-redux";
import {RootDispatch, RootState} from "../state/rootStore";
import {addElectiveChoices, ElectiveChoice, removeElectiveChoices} from "../state/electiveCoursesSlice";
import {CourseTableType} from "./CourseTableType";
import {DecisionType} from "../backend/entity/elective/Choice";
import {CourseWithSemesters} from "../backend/entity/grid/CourseWithSemesters";
import CourseDetails from "./CourseDetails";
import {
    DELETE_ELECTIVE_CHOICE,
    INSERT_ELECTIVE_CHOICES_BY_PERSON_ID
} from "../backend/service/ElectiveService";
import {useMutation} from "@apollo/client";
import {UserState} from "../state/userSlice";
import {notification} from 'antd';

interface CourseTableProps {
    tableType: CourseTableType
    title: String
    specialityId: number
    electiveGroup: string
    courses: CourseWithSemesters[]
    currentStudentSemester: number
    areChoicesDisabled: boolean
    howManyFutureSemesters: number
    yearSemesterId: number
}

export function CourseTable({
                                tableType,
                                title,
                                specialityId,
                                electiveGroup,
                                courses,
                                currentStudentSemester,
                                areChoicesDisabled,
                                howManyFutureSemesters,
                                yearSemesterId
                            }: CourseTableProps) {
    const {t} = useTranslation()
    const dispatch = useDispatch<RootDispatch>()
    const electiveChoices = useSelector<RootState, ElectiveChoice[]>(state => state.electiveCourseReducer.electiveChoices)
    const user = useSelector<RootState, UserState>(state => state.userReducer)
    const person_id = user.personId
    const [deleteElectiveChoice] = useMutation<any>(DELETE_ELECTIVE_CHOICE)
    const [insertElectiveChoices] = useMutation<any>(INSERT_ELECTIVE_CHOICES_BY_PERSON_ID)

    function openElectiveChoicesSaveErrorNotification(error: Error) {
        notification['error']({
            message: 'Problem with saving the elective choice',
            description:
                'An error occurred while saving the elective choices data: ' + error.message,
        })
    }

    function openElectiveChoicesDeleteErrorNotification(error: Error) {
        notification['error']({
            message: 'Problem with deleting the elective choice',
            description:
                'An error occurred while deleting the elective choices data: ' + error.message,
        })
    }

    function openElectiveChoicesSaveSuccessNotification() {
        notification['success']({
            message: t('elective_choice.saved'),
            description: t('elective_choice.saved_desc'),
            duration: 3
        })
    }

    function openElectiveChoicesDeleteSuccessNotification() {
        notification['success']({
            message: t('elective_choice.deleted'),
            description: t('elective_choice.deleted_desc'),
            duration: 3
        })
    }

    const columns = [
        {
            title: capitalizeFirstLetter(t('course.decision')),
            render: (record: CourseWithSemesters) => {
                if (electiveChoices.some(ec => ec.courseId === record.id)) {
                    const decision: DecisionType = (electiveChoices.filter(ec => ec.courseId === record.id).at(0)?.decision) || "none"
                    switch (decision) {
                        case "accept":
                            return <Tag color="green">{t('decision.accept')}</Tag>
                        case "none":
                            return <Tag>{t('decision.none')}</Tag>
                        case "reject":
                            return <Tag color="red">{t('decision.reject')}</Tag>
                    }

                } else {
                    return ''
                }
            },
            key: 'decision',
            width: '6%',
        },
        {
            title: capitalizeFirstLetter(t('course.description')),
            render: (record: CourseWithSemesters) => {
                return (
                    <Descriptions size="small">
                        <Descriptions.Item contentStyle={{fontWeight: "bold"}}
                                           label={capitalizeFirstLetter(t('course.name'))}>{record.name}</Descriptions.Item>
                        {record.course_semesters &&
                            <Descriptions.Item label={capitalizeFirstLetter(t('course.ects_sum'))}>
                                {record.course_semesters.map(cs => cs.ects).reduce((partialSum, a) => partialSum + a, 0)}
                            </Descriptions.Item>}
                        {record.course_semesters &&
                            <Descriptions.Item label={capitalizeFirstLetter(t('course.hours_sum'))}>
                                {record.course_semesters.map(cs => cs.hours).reduce((partialSum, a) => partialSum + a, 0)}
                            </Descriptions.Item>
                        }
                        {record.course_type &&
                            <Descriptions.Item
                                label={capitalizeFirstLetter(t('course.type'))}>{record.course_type.name}
                            </Descriptions.Item>
                        }
                        {record.method_of_conducting &&
                            <Descriptions.Item
                                label={capitalizeFirstLetter(t('course.method_of_conducting'))}>{record.method_of_conducting.name}
                            </Descriptions.Item>
                        }
                    </Descriptions>
                )
            },
            key: 'name',
            width: '94%',
        },
    ];

    const rowSelection = {
        selectedRowKeys: electiveChoices
            .filter(ec => ec.specialityId === specialityId)
            .map(ec => ec.courseId),
        onChange: (selectedRowKeys: React.Key[], selectedRows: CourseWithSemesters[]) => {

            const choices_to_remove: ElectiveChoice[] = electiveChoices
                .filter(ec => (ec.electiveGroup.toLowerCase() === electiveGroup.toLowerCase() && ec.specialityId === specialityId))
                .filter(ec => !selectedRows.map(c => c.id).includes(ec.courseId))
                .map(ec => ({
                    specialityId: specialityId,
                    courseId: ec.courseId,
                    courseEcts: ec.courseEcts,
                    decision: ec.decision,
                    electiveGroup: ec.electiveGroup
                }))

            const choices_to_add: ElectiveChoice[] = selectedRows
                .filter(sc => !electiveChoices
                    .filter(ec => (ec.electiveGroup.toLowerCase() === electiveGroup.toLowerCase() && ec.specialityId === specialityId))
                    .map(c => c.courseId).includes(sc.id))
                .map(c => ({
                    specialityId,
                    courseId: c.id,
                    courseEcts: c.course_semesters.map(cs => cs.ects).reduce((sum, el) => sum + el, 0),
                    decision: 'none',
                    electiveGroup: c.elective_group
                }))

            switch (tableType) {
                default:
                    // remove
                    if (choices_to_remove.length > 0) {
                        dispatch(removeElectiveChoices(choices_to_remove))
                        const mutations = choices_to_remove.map(ec => (
                            deleteElectiveChoice(
                                {
                                    variables: {
                                        person_id,
                                        course_id: ec.courseId,
                                        speciality_id: ec.specialityId,
                                    }
                                }
                            )))
                        Promise.allSettled(
                            mutations
                        ).then(() => {
                            openElectiveChoicesDeleteSuccessNotification()

                        }).catch(
                            (error: Error) => {
                                openElectiveChoicesDeleteErrorNotification(error)
                            })
                    }
                    // add
                    if (choices_to_add.length > 0) {
                        dispatch(addElectiveChoices(choices_to_add))
                        insertElectiveChoices({
                            variables: {
                                elective_choices_to_add: choices_to_add.map(ec => ({
                                    person_id,
                                    course_id: ec.courseId,
                                    speciality_id: ec.specialityId,
                                    elective_group: ec.electiveGroup,
                                    year_semester_id: yearSemesterId
                                }))
                            }
                        }).then(() => {
                                openElectiveChoicesSaveSuccessNotification()

                            }).catch(
                                (error: Error) => {
                                    openElectiveChoicesSaveErrorNotification(error)
                                })
                    }

            }
        },
        getCheckboxProps: () => ({disabled: areChoicesDisabled}),
        hideSelectAll: true
    };

    const coursesWithCorrectSemester = courses
        .filter(c => c.course_semesters.some(cs => (cs.number === currentStudentSemester) || (cs.number === currentStudentSemester + howManyFutureSemesters)))
        .map(({course_semesters, ...rest}) => {
                const filteredSemesters = course_semesters
                    .filter(cs => (cs.number === currentStudentSemester) || cs.number === currentStudentSemester + howManyFutureSemesters)
                return ({...rest, course_semesters: filteredSemesters})
            }
        )
        .sort((a, b) => a.name.localeCompare(b.name))

    return (
        <>
            {coursesWithCorrectSemester.length > 0 &&
                <>
                    <br/>
                    <Table
                        title={() => <strong>{title}</strong>}
                        size="small"
                        showHeader={true}
                        bordered
                        rowSelection={{
                            type: tableType === CourseTableType.ONE ? 'radio' : 'checkbox',
                            ...rowSelection,
                        }}
                        dataSource={coursesWithCorrectSemester}
                        columns={columns}
                        rowKey={'id'}
                        pagination={false}
                        expandable={{
                            expandedRowRender: record => <CourseDetails key={record.id} record={record}/>,
                            rowExpandable: record => record.course_semesters.length !== 0
                        }}
                    />
                </>}
        </>
    )
}